import { useCallback, useEffect, useMemo, useState } from 'react';

import { INTEGRATION_POLL_MS } from '../../../shared/domain/integrations/poll-interval.js';
import { useRevalidateOnVisible } from '../../app/hooks/use-revalidate-on-visible.js';
import { reportClientError } from '../../app/errors/report-client-error.js';
import * as profileApi from './api/profile.api.js';
import { ProfileContext } from './profile-context.js';

export function ProfileProvider({ children }) {
  const [profile, setProfile] = useState(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);

  const load = useCallback(async (signal) => {
    setStatus('loading');
    try {
      const result = await profileApi.fetchProfile({ signal });
      setProfile(result.profile);
      setError(null);
      setStatus('ready');
    } catch (caught) {
      if (signal?.aborted) return;
      reportClientError(caught, { scope: 'profile.load' });
      setError(caught.message ?? 'Could not load your profile.');
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  // Loaded once on mount and otherwise only updated by this tab's own `save` - a
  // profile edited on another device stays stale here until something reloads it.
  useRevalidateOnVisible(load, { intervalMs: INTEGRATION_POLL_MS });

  const save = useCallback(async (fields) => {
    const result = await profileApi.saveProfile({ profile: fields, version: profile?.version ?? 0 });
    setProfile(result.profile);
    return result.profile;
  }, [profile]);

  const value = useMemo(() => ({ profile, status, error, save, reload: load }),
    [profile, status, error, save, load]);
  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
}
